import { useError } from "../contexts/ErrorContext";
import { AxiosError } from "axios";

export interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
  status?: number;
}

export const useApiError = () => {
  const { showError, showValidationError } = useError();

  const handleApiError = (
    error: unknown,
    defaultMessage: string = "An error occurred"
  ) => {
    if (error instanceof Error) {
      // Handle Axios errors
      if (error.name === "AxiosError") {
        const axiosError = error as AxiosError<ApiErrorResponse>;
        const response = axiosError.response;

        if (response?.data) {
          const { message, errors, status } = response.data;

          // Handle validation errors
          if (errors && Object.keys(errors).length > 0) {
            const formattedErrors: Record<string, string> = {};
            Object.keys(errors).forEach((key) => {
              formattedErrors[key] = errors[key].join(", ");
            });
            showValidationError(formattedErrors);
            return;
          }

          // Handle specific error messages
          if (message) {
            showError(message);
            return;
          }
        }

        // Handle HTTP status errors
        if (response?.status) {
          switch (response.status) {
            case 400:
              showError("Bad Request - Please check your input data");
              break;
            case 401:
              showError("Unauthorized - Please login again");
              break;
            case 403:
              showError(
                "Forbidden - You do not have permission to perform this action"
              );
              break;
            case 404:
              showError("Not Found - The requested resource was not found");
              break;
            case 409:
              showError("Conflict - This record already exists");
              break;
            case 422:
              showError("Validation Error - Please check your input data");
              break;
            case 500:
              showError("Server Error - Please try again later");
              break;
            case 502:
              showError("Bad Gateway - Server is temporarily unavailable");
              break;
            case 503:
              showError(
                "Service Unavailable - Server is temporarily unavailable"
              );
              break;
            default:
              showError(`HTTP Error ${response.status} - ${defaultMessage}`);
          }
          return;
        }

        // Handle network errors
        if (axiosError.code === "NETWORK_ERROR") {
          showError("Network Error - Please check your internet connection");
          return;
        }

        if (axiosError.code === "ECONNABORTED") {
          showError("Request Timeout - Please try again");
          return;
        }
      }

      // Handle generic errors
      showError(error.message || defaultMessage);
    } else {
      // Handle unknown errors
      showError(defaultMessage);
    }
  };

  const handleMutationError = (
    error: unknown,
    operation: string = "operation"
  ) => {
    handleApiError(error, `Failed to ${operation}`);
  };

  const handleQueryError = (error: unknown, resource: string = "data") => {
    handleApiError(error, `Failed to load ${resource}`);
  };

  const handleUploadError = (error: unknown) => {
    handleApiError(error, "Failed to upload file");
  };

  const handleDeleteError = (error: unknown, resource: string = "item") => {
    handleApiError(error, `Failed to delete ${resource}`);
  };

  const handleCreateError = (error: unknown, resource: string = "item") => {
    handleApiError(error, `Failed to create ${resource}`);
  };

  const handleUpdateError = (error: unknown, resource: string = "item") => {
    handleApiError(error, `Failed to update ${resource}`);
  };

  const handleLoginError = (error: unknown) => {
    handleApiError(error, "Login failed");
  };

  const handleRegistrationError = (error: unknown) => {
    handleApiError(error, "Registration failed");
  };

  const handleEmailError = (error: unknown) => {
    handleApiError(error, "Failed to send email");
  };

  const handlePdfError = (error: unknown) => {
    handleApiError(error, "Failed to generate PDF");
  };

  return {
    handleApiError,
    handleMutationError,
    handleQueryError,
    handleUploadError,
    handleDeleteError,
    handleCreateError,
    handleUpdateError,
    handleLoginError,
    handleRegistrationError,
    handleEmailError,
    handlePdfError,
  };
};
